import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createTeamMemberRequest,
  getTeamMembers,
  removeTeamMember,
  updateTeamMemberRequest,
} from "../../../../services/api/landing/teamMembers/teamMembersApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Team member created successfully!",
    ar: "تم إنشاء عضو الفريق بنجاح!",
  },
  addError: {
    en: "Failed to create team member.",
    ar: "فشل في إنشاء عضو الفريق.",
  },
  update: {
    en: "Team member updated successfully!",
    ar: "تم تحديث عضو الفريق بنجاح!",
  },
  updateError: {
    en: "Failed to update team member.",
    ar: "فشل في تحديث عضو الفريق.",
  },
  delete: {
    en: "Team member deleted successfully!",
    ar: "تم حذف عضو الفريق بنجاح!",
  },
  deleteError: {
    en: "Failed to delete team member.",
    ar: "فشل في حذف عضو الفريق.",
  },
};

export const fetchTeamMembers = createAsyncThunk(
  "teamMembers/fetchTeamMembers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTeamMembers();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch team members");
    }
  },
);

export const createTeamMember = createAsyncThunk(
  "teamMembers/createTeamMember",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createTeamMemberRequest(formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create team member");
    }
  },
);

export const updateTeamMember = createAsyncThunk(
  "teamMembers/updateTeamMember",
  async ({ teamMemberId, formData }, { rejectWithValue }) => {
    try {
      const response = await updateTeamMemberRequest(teamMemberId, formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update team member");
    }
  },
);

export const deleteTeamMember = createAsyncThunk(
  "teamMembers/deleteTeamMember",
  async (teamMemberId, { rejectWithValue }) => {
    try {
      await removeTeamMember(teamMemberId);
      return teamMemberId;
    } catch (error) {
      return rejectWithValue("Failed to delete team member");
    }
  },
);

const teamMembersSlice = createSlice({
  name: "teamMembers",
  initialState: {
    teamMemberData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.teamMemberData = action.payload;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTeamMember.fulfilled, (state, action) => {
        state.teamMemberData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createTeamMember.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateTeamMember.fulfilled, (state, action) => {
        const index = state.teamMemberData.findIndex(
          (member) => member.id === action.payload.id,
        );
        if (index !== -1) {
          state.teamMemberData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateTeamMember.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteTeamMember.fulfilled, (state, action) => {
        state.teamMemberData = state.teamMemberData.filter(
          (member) => member.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteTeamMember.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default teamMembersSlice.reducer;
